"use client";
import React, { useState } from "react";
import Image from "next/image";
import DefaultImage from "@/mocks/assets/defaultImage.png";
import { SingleProductImageFocusedPrice } from "./SingleProductImageFocusedPrice";
import { giftCardItem } from "@/utils/giftCardUtils";
import CloverGiftCardImage from "./cloverGiftCard.png";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";
import { IconSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";

export interface SingleProductProps {
  product: any;
}

export const SingleProductImageFocused = ({ product }: SingleProductProps) => {
  const t = useTranslations("SingleProductDescriptionFocused");
  const [loading, setLoading] = useState(true);
  const isGiftCardProduct = product.id === giftCardItem.id;
  const productImage =
    product.images && product.images.length > 0
      ? product.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, product.images[0])
      : null;

  function getProductImageDetails(
    isGiftCardProduct: boolean,
    productImage: any
  ) {
    if (isGiftCardProduct) {
      return {
        src: CloverGiftCardImage,
        dataTestId: pom.products.giftCardImage,
      };
    } else {
      if (productImage) {
        return {
          src: productImage.source,
          dataTestId: pom.landingPage.productCardImage,
        };
      } else {
        return {
          src: DefaultImage,
          dataTestId: pom.landingPage.productCardImage,
        };
      }
    }
  }

  const imageDetails = getProductImageDetails(isGiftCardProduct, productImage);

  return (
    <div key={product.id} className={["flex", "flex-col", "gap-5"].join(" ")}>
      <div className="relative overflow-hidden rounded-md bg-gray-200 group-hover:opacity-75">
        <div className="relative pb-[133.33%]">
          <div className="absolute w-full h-full">
            {loading && (
              <IconSkeleton
                width={300}
                height={400}
                styles={{ borderRadius: "0 0.25rem 0.25rem 0" }}
              />
            )}
            <div
              className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 ${
                loading ? "opacity-0" : "opacity-100"
              }`}
            >
              <Image
                data-testid={applyDataTestId(imageDetails.dataTestId)}
                width={300}
                height={400}
                sizes="100vw"
                alt={product.imageAlt || t("productImageAlt")}
                src={imageDetails.src}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full object-cover"
                onLoad={() => setLoading(false)}
                onError={() => setLoading(false)}
              />
            </div>
          </div>
          {!product.available && (
            <div className="absolute inset-0 bg-black opacity-40" />
          )}
        </div>
      </div>
      <div className={["flex", "flex-col", "gap-2"].join(" ")}>
        <p
          data-testid={applyDataTestId(pom.landingPage.productCardName)}
          className="truncate text-base font-normal sm:leading-[27px]"
        >
          {product.name}
        </p>
        <SingleProductImageFocusedPrice
          product={product}
          isGiftCardProduct={isGiftCardProduct}
        />
      </div>
    </div>
  );
};
